import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SearchQuery } from '../models/search-query.model';
import {
  FlyingClubFinderFilter,
  FlyingClubFacetOption,
  FLYING_CLUB_MEMBERSHIP_OPTIONS,
  FLYING_CLUB_BENEFITS_OPTIONS,
  FLYING_CLUB_FLYING_OPTIONS
} from '../../shared/models/flying-club-finder-filter.model';
import { FlyingClubFinderFiltersService } from '../../shared/services/flying-club-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';

/**
 * Group keys on FlyingClubFinderFilter that hold an array of selected tag names.
 */
type FacetGroup = 'membership' | 'benefits' | 'flying';

/**
 * Filter UI for the Flying Club Finder, mirroring {@link EventFilterListComponent}. Renders a keyword
 * field, club-type checkboxes, and the Membership / Benefits / Types-of-Flying facet groups. Reads and
 * writes the shared {@link FlyingClubFinderFiltersService} state and emits `filtersChange`.
 */
@Component({
  selector: 'app-flying-club-finder-filter-list',
  templateUrl: './flying-club-finder-filter-list.component.html',
  styleUrls: ['./flying-club-finder-filter-list.component.scss']
})
export class FlyingClubFinderFilterListComponent implements OnInit, OnDestroy {
  @Input() compact: boolean;
  @Input() query: SearchQuery;
  @Output() filtersChange = new EventEmitter<void>();

  readonly membershipOptions = FLYING_CLUB_MEMBERSHIP_OPTIONS;
  readonly benefitsOptions = FLYING_CLUB_BENEFITS_OPTIONS;
  readonly flyingOptions = FLYING_CLUB_FLYING_OPTIONS;

  flyingClubFinderFilter = new FlyingClubFinderFilter();
  private _subscription: Subscription;

  /**
   * True when a club name has been entered but is too short to count toward a search. Mirrors the
   * `clubKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while
   * the entered name would be ignored.
   */
  get nameTooShort(): boolean {
    const trimmed = this.flyingClubFinderFilter.name ? this.flyingClubFinderFilter.name.trim() : '';
    return trimmed.length > 0 && trimmed.length < 3;
  }

  constructor(private _flyingClubFinderFiltersService: FlyingClubFinderFiltersService,
              private _analyticsService: AnalyticsService) { }

  ngOnInit() {
    this._subscription = this._flyingClubFinderFiltersService.flyingClubFinderFilter.subscribe(f => {
      this.flyingClubFinderFilter = f;
    });
    this._analyticsService.pageView('airport_landingpage_flyingclub', 'Flying Club Finder Landing Page filters');
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  isFacetSelected(group: FacetGroup, value: string): boolean {
    return this.flyingClubFinderFilter[group].indexOf(value) !== -1;
  }

  onToggleFacet(group: FacetGroup, value: string, checked: boolean) {
    const selected = this.flyingClubFinderFilter[group];
    const i = selected.indexOf(value);
    if (checked && i === -1) {
      selected.push(value);
    } else if (!checked && i !== -1) {
      selected.splice(i, 1);
    }
    this._emit();
  }

  onSelectAll(group: FacetGroup, options: FlyingClubFacetOption[]) {
    this.flyingClubFinderFilter[group] = options.map(o => o.value);
    this._emit();
  }

  onClearAll(group: FacetGroup) {
    this.flyingClubFinderFilter[group] = [];
    this._emit();
  }

  onToggleClubType() {
    this._emit();
  }

  onNameChange() {
    this._emit();
  }

  private _emit() {
    // Keep the query's filter in sync as the user edits, so a club name entered here counts toward search
    // validation (see SearchQuery.validate) even before the query is serialized/submitted.
    if (this.query) {
      this.query.flyingClubFinderFilter = this.flyingClubFinderFilter;
    }
    this._flyingClubFinderFiltersService.updateFlyingClubFinderFilters(this.flyingClubFinderFilter);
    this.filtersChange.emit();
  }
}
